import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Card, Row, Col, Statistic, List, Tag, Spin, message } from 'antd';
import {
  FileTextOutlined, AuditOutlined,
  ClockCircleOutlined, ArrowRightOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import AppHeader from '../components/AppHeader';
import { useTheme } from '../context/ThemeContext';

const { Content } = Layout;

const deviceTypes = [
  { key: 'belt_metal_detector', name: '皮带式金属检测机', icon: '🔩' },
];

const HomePage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isDarkMode, getBackgroundColor, getTextColor, getTextColorSecondary } = useTheme();

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';
  const isDesignerLeader = user.role === 'designer_leader';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, logsRes] = await Promise.all([
        api.get('/home/stats'),
        api.get('/home/recent-logs'),
      ]);
      setStats(statsRes.data);
      setRecentLogs(logsRes.data);
    } catch (err) {
      message.error('获取首页数据失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <Layout style={{ minHeight: '100vh', background: getBackgroundColor() }}>
        <AppHeader />
        <Content style={{ padding: 24 }}>
          <div style={{ textAlign: 'center', padding: 100 }}>
            <Spin size="large" />
          </div>
        </Content>
      </Layout>
    );
  }

  const statCardStyle = {
    cursor: 'pointer',
    borderRadius: 8,
    border: isDarkMode ? '1px solid #434343' : '1px solid #f0f0f0',
  };

  const statValueStyle = (color) => ({
    color: color,
    fontSize: 32,
    fontWeight: 'bold',
  });

  return (
    <Layout style={{ minHeight: '100vh', background: getBackgroundColor() }}>
      <AppHeader />
      <Content style={{ padding: 24 }}>
        {/* 统计概览 */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={isAdmin ? 8 : 12}>
            <Card hoverable onClick={() => navigate('/drawings/belt_metal_detector')} style={statCardStyle}>
              <Statistic
                title={<span style={{ color: getTextColorSecondary(), fontSize: 14 }}>图纸总数</span>}
                value={stats?.totalDrawings || 0}
                prefix={<FileTextOutlined style={{ color: '#1677ff', fontSize: 24 }} />}
                valueStyle={statValueStyle('#1677ff')}
              />
            </Card>
          </Col>

          {(isAdmin || isDesignerLeader) && (
            <Col xs={24} sm={12} lg={isAdmin ? 8 : 12}>
              <Card hoverable onClick={() => navigate('/drawing-approvals')} style={statCardStyle}>
                <Statistic
                  title={<span style={{ color: getTextColorSecondary(), fontSize: 14 }}>待审批</span>}
                  value={stats?.pendingApprovals || 0}
                  prefix={<AuditOutlined style={{ color: '#faad14', fontSize: 24 }} />}
                  valueStyle={statValueStyle('#faad14')}
                />
              </Card>
            </Col>
          )}

          {isAdmin && (
            <Col xs={24} sm={12} lg={8}>
              <Card hoverable onClick={() => navigate('/system-logs')} style={statCardStyle}>
                <Statistic
                  title={<span style={{ color: getTextColorSecondary(), fontSize: 14 }}>今日操作</span>}
                  value={stats?.todayLogs || 0}
                  prefix={<ClockCircleOutlined style={{ color: '#52c41a', fontSize: 24 }} />}
                  valueStyle={statValueStyle('#52c41a')}
                />
              </Card>
            </Col>
          )}
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
          <Col xs={24} lg={isAdmin ? 12 : 24}>
            {/* 去掉 extra 属性，移除右上角箭头 */}
            <Card 
              title={<span style={{ color: getTextColor(), fontWeight: 600, fontSize: 16 }}>设备类型</span>}
              style={{ 
                borderRadius: 8,
                border: isDarkMode ? '1px solid #434343' : '1px solid #f0f0f0',
              }}
              headStyle={{ borderBottom: isDarkMode ? '1px solid #434343' : '1px solid #f0f0f0' }}
            >
              <List
                dataSource={deviceTypes}
                renderItem={(item) => (
                  <List.Item
                    style={{ 
                      cursor: 'pointer',
                      padding: '16px 0',
                      borderBottom: isDarkMode ? '1px solid #2c2c2c' : '1px solid #f0f0f0',
                    }}
                    onClick={() => navigate(`/drawings/${item.key}`)}
                  >
                    <List.Item.Meta
                      avatar={<span style={{ fontSize: 32 }}>{item.icon}</span>}
                      title={<span style={{ color: getTextColor(), fontSize: 16, fontWeight: 500 }}>{item.name}</span>}
                      description={<span style={{ color: getTextColorSecondary() }}>共 {stats?.drawingCounts?.[item.key] || 0} 份图纸</span>}
                    />
                    {/* 保留列表项右侧箭头 */}
                    <ArrowRightOutlined style={{ color: getTextColorSecondary(), fontSize: 16 }} />
                  </List.Item>
                )}
              />
            </Card>
          </Col>

          {isAdmin && (
            <Col xs={24} lg={12}>
              <Card 
                title={<span style={{ color: getTextColor(), fontWeight: 600, fontSize: 16 }}>最近操作日志</span>}
                extra={
                  <a onClick={() => navigate('/system-logs')} style={{ color: '#1677ff' }}>
                    查看全部
                  </a>
                }
                style={{ 
                  borderRadius: 8,
                  border: isDarkMode ? '1px solid #434343' : '1px solid #f0f0f0',
                }}
                headStyle={{ borderBottom: isDarkMode ? '1px solid #434343' : '1px solid #f0f0f0' }}
              >
                <List
                  size="small"
                  dataSource={recentLogs}
                  renderItem={(log) => (
                    <List.Item style={{ 
                      borderBottom: isDarkMode ? '1px solid #2c2c2c' : '1px solid #f0f0f0',
                      padding: '10px 0',
                    }}>
                      <List.Item.Meta
                        title={
                          <span style={{ color: getTextColor() }}>
                            <Tag color="blue" size="small">{log.action}</Tag>
                            <span style={{ marginLeft: 8 }}>{log.operator}</span>
                            <span style={{ color: getTextColorSecondary(), marginLeft: 8 }}>- {log.target || '-'}</span>
                          </span>
                        }
                        description={
                          <span style={{ color: getTextColorSecondary(), fontSize: 12 }}>
                            {log.created_at ? new Date(log.created_at).toLocaleString('zh-CN') : '-'}
                          </span>
                        }
                      />
                    </List.Item>
                  )}
                  locale={{ emptyText: <span style={{ color: getTextColorSecondary() }}>暂无日志</span> }}
                />
              </Card>
            </Col>
          )}
        </Row>
      </Content>
    </Layout>
  );
};

export default HomePage;